// @flow

import { makeAutoObservable, runInAction, computed } from 'mobx';
import jobsApi from 'core/api/jobs.api';
import companiesApi from 'core/api/companies.api';
import placesApi from 'core/api/places.api';
import placeApi from 'core/api/place.api';

class JobsStore {
  jobs: any[] = [];
  totalRecords: number = 0;
  counts: any = {};
  perPage: number = 9;
  loading: boolean = false;
  searchParams: any = {};
  page: number = 1;
  relevantCompanies: any[] = [];
  relevantCompaniesFetching: boolean = false;
  showFilters: boolean = false;
  showAlerts: boolean = false;
  showInput: boolean = false;
  places: any[] = [];
  placesFetching: boolean = false;
  place: {} = {};
  placeName: string = '';
  placeFetching: boolean = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  getThirdPartyParams = (params: any) => {
    const updatedParams = { ...params, filter: {} };

    ['query', 'place_id', 'distance', 'category_ids', 'industry_ids', 'tag_ids'].forEach(param => {
      updatedParams.filter[param] = params.filter[param];
    });

    return updatedParams;
  };

  @computed
  get pageCount(): number {
    return this.totalRecords / this.perPage;
  }

  toggleVisibility(name: string) {
    if (name === 'filters') this.showFilters = !this.showFilters;
    if (name === 'alerts') this.showAlerts = !this.showAlerts;
    if (name === 'input') this.showInput = !this.showInput;
  }

  async getJobs(params: {} = {}) {
    runInAction(() => {
      this.loading = true;
    });

    const { data, meta } = await jobsApi.fetch(params);

    runInAction(() => {
      this.loading = false;
      this.jobs = data;
      this.counts = meta.counts;
      this.totalRecords = meta.total_records;
      this.showInput = false;
    });
  }

  async getRelevantCompanies(params: any = {}) {
    if (params.filter.query) {
      this.relevantCompaniesFetching = true;
      const { data } = await companiesApi.fetch(params);

      runInAction(() => {
        this.relevantCompanies = data;
        this.relevantCompaniesFetching = false;
      });
    } else this.relevantCompanies = [];
  }

  async getJobsWithThirdParty(params: any = {}) {
    runInAction(() => {
      this.loading = true;
    });

    const { page: { limit, offset } } = params;
    const thirdPartyParams = this.getThirdPartyParams(params);

    const { data, meta } = await jobsApi.fetch(params);

    const currentPage = (limit + offset) / limit;
    const jobsMaxPage = Math.ceil(meta.total_records / limit);

    if (currentPage < jobsMaxPage) {
      const thirdPartyJobs = await jobsApi.fetchThirdParty({ ...thirdPartyParams, page: { limit: 1, offset: 0 } });

      runInAction(() => {
        this.jobs = data;
        this.counts = meta.counts;
        this.totalRecords = meta.total_records + thirdPartyJobs.meta.total_records;
      });
    } else if (currentPage === jobsMaxPage) {
      const thirdPartyJobs = await jobsApi.fetchThirdParty({
        ...thirdPartyParams, page: {
          limit: limit + offset - meta.total_records,
          offset: 0
        }
      });

      runInAction(() => {
        this.jobs = [...data, ...thirdPartyJobs.data];
        this.counts = meta.counts;
        this.totalRecords = meta.total_records + thirdPartyJobs.meta.total_records;
      });
    } else if (currentPage > jobsMaxPage) {
      const thirdPartyJobs = await jobsApi.fetchThirdParty({
        ...thirdPartyParams, page: {
          limit,
          offset: offset - meta.total_records
        }
      });

      runInAction(() => {
        this.jobs = thirdPartyJobs.data;
        this.counts = meta.counts;
        this.totalRecords = meta.total_records + thirdPartyJobs.meta.total_records;
      });
    }
    runInAction(() => {
      this.loading = false;
    });
  }

  async getPlaces(params: {} = {}) {
    this.placesFetching = true;
    const { data } = await placesApi.fetch(params);

    runInAction(() => {
      this.places = data;
      this.placesFetching = false;
    });
  }

  async getPlaceName(id: number | string) {
    this.placeFetching = true;
    const { data } = await placeApi.get(id);

    runInAction(() => {
      this.placeName = data.name;
      this.placeFetching = false;
    });
  }
}

export default new JobsStore();
